import React from 'react';
import { ArrowRightIcon, CheckCircle2Icon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Locale, useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
type Copy = Record<Locale, string>;
type PageData = {
  eyebrow: Copy;
  title: Copy;
  description: Copy;
  image: string;
  sections: {
    title: Copy;
    body: Copy;
  }[];
};
const pageData: Record<string, PageData> = {
  '/about': {
    eyebrow: {
      fr: 'À propos de Société Supermarches Charentais',
      en: 'About Société Supermarches Charentais'
    },
    title: {
      fr: 'La distribution alimentaire et non-alimentaire au service du commerce.',
      en: 'Food and non-food distribution, built for retail.'
    },
    description: {
      fr: 'Société Supermarches Charentais est un distributeur spécialisé de produits alimentaires et non-alimentaires pour les supermarchés, détaillants, professionnels de l’hôtellerie et collectivités. Aux côtés de Centre Leclerc, nous proposons une offre large et fiable.',
      en: 'Société Supermarches Charentais is a specialist distributor of food and non-food products for supermarkets, retailers, hospitality businesses and institutions. Alongside Centre Leclerc, we offer professional customers a broad, dependable range.'
    },
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1600&q=90',
    sections: [{
      title: {
        fr: 'Une offre complète',
        en: 'A complete product range'
      },
      body: {
        fr: 'Des produits frais, de l’épicerie et des boissons à l’entretien, l’équipement, au linge de maison, au scolaire et aux essentiels professionnels : nos catégories répondent à chaque besoin.',
        en: 'From fresh food, pantry staples and beverages to home care, equipment, home textiles, school supplies and business essentials, our categories cover every need.'
      }
    }, {
      title: {
        fr: 'Un partenaire pour le commerce et les professionnels',
        en: 'A partner for retail and business'
      },
      body: {
        fr: 'Nos équipes accompagnent les supermarchés, commerces indépendants, hôtels, restaurants, écoles, bureaux et collectivités avec des solutions sur demande et devis.',
        en: 'Our team supports supermarkets, independent retailers, hotels, restaurants, schools, offices and institutions with quotation-led supply solutions.'
      }
    }]
  },
  '/brands': {
    eyebrow: {
      fr: 'Marques de confiance',
      en: 'Trusted brands'
    },
    title: {
      fr: 'Les marques déjà présentes dans votre quotidien.',
      en: 'The names your home already knows.'
    },
    description: {
      fr: 'Retrouvez un large choix de grandes marques internationales et de spécialistes reconnus dans tous nos rayons.',
      en: 'Explore a wide mix of international favourites and dependable specialist brands across every department.'
    },
    image: 'https://images.unsplash.com/photo-1580915411954-282cb1b0d780?auto=format&fit=crop&w=1600&q=90',
    sections: [{
      title: {
        fr: 'Alimentation & boissons',
        en: 'Food & refreshment'
      },
      body: {
        fr: 'Nestlé, Nescafé, Coca-Cola, Pepsi, Kellogg’s et bien d’autres.',
        en: 'Nestlé, Nescafé, Coca-Cola, Pepsi, Kellogg’s and more.'
      }
    }, {
      title: {
        fr: 'Maison & équipement',
        en: 'Home & technology'
      },
      body: {
        fr: 'Samsung, LG, Sony, Philips, Bosch, Canon et les grandes marques de la maison.',
        en: 'Samsung, LG, Sony, Philips, Bosch, Canon and leading household brands.'
      }
    }]
  },
  '/gallery': {
    eyebrow: {
      fr: 'Dans votre Centre Leclerc',
      en: 'Inside Centre Leclerc'
    },
    title: {
      fr: 'Découvrez le magasin avant votre visite.',
      en: 'See the store before you visit.'
    },
    description: {
      fr: 'Des allées lumineuses, un marché frais généreux et des rayons spécialisés pensés pour des courses simples et agréables.',
      en: 'Bright aisles, a generous fresh market and specialist home departments designed for easy, satisfying shopping.'
    },
    image: 'https://images.unsplash.com/photo-1601598851547-4302969d0614?auto=format&fit=crop&w=1600&q=90',
    sections: [{
      title: {
        fr: 'Le marché frais',
        en: 'Fresh food hall'
      },
      body: {
        fr: 'Fruits, légumes, boulangerie, viandes et produits de la mer dans un espace vivant et gourmand.',
        en: 'Fresh fruit, vegetables, bakery, meat and seafood in one vibrant market space.'
      }
    }, {
      title: {
        fr: 'Maison & quotidien',
        en: 'Home & lifestyle'
      },
      body: {
        fr: 'De la cuisine au linge de maison, en passant par les appareils et le scolaire.',
        en: 'From cookware and bedding to electricals and school essentials.'
      }
    }]
  },
  '/news': {
    eyebrow: {
      fr: 'Actualités & offres',
      en: 'News & promotions'
    },
    title: {
      fr: 'Les nouveautés de votre Centre Leclerc.',
      en: 'What’s happening at Centre Leclerc.'
    },
    description: {
      fr: 'Nouveaux arrivages saisonniers, actualités des rayons et informations pour nos clients professionnels.',
      en: 'New seasonal arrivals, department updates and business information from our stores.'
    },
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=90',
    sections: [{
      title: {
        fr: 'Arrivages de saison',
        en: 'Seasonal arrivals'
      },
      body: {
        fr: 'Découvrez tout au long de l’année nos nouveautés maison, jardin et rentrée.',
        en: 'Discover new home, garden and school essentials throughout the year.'
      }
    }, {
      title: {
        fr: 'Actualités professionnelles',
        en: 'Business updates'
      },
      body: {
        fr: 'Nouveaux services et mises en avant de catégories pour nos clients professionnels.',
        en: 'New services and category highlights for professional customers.'
      }
    }]
  }
};
export function StandardPage() {
  const {
    pathname
  } = useLocation();
  const {
    locale
  } = useLocale();
  const data = pageData[pathname] ?? pageData['/about'];
  const request = locale === 'fr' ? 'Demander des informations' : 'Request information';
  return <SiteLayout data-id="element-249">
      <main data-id="element-250">
        <section className="bg-[#e8f2f3]" data-id="element-251">
          <div className="mx-auto grid max-w-[1280px] items-stretch md:grid-cols-2" data-id="element-252">
            <div className="flex flex-col justify-center px-6 py-20 md:px-14" data-id="element-253">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-254">
                {data.eyebrow[locale]}
              </p>
              <h1 className="mt-5 text-5xl font-extrabold leading-tight tracking-[-0.05em] text-[#102b43]" data-id="element-255">
                {data.title[locale]}
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-8 text-slate-600" data-id="element-256">
                {data.description[locale]}
              </p>
              <Link to="/contact" className="mt-8 inline-flex w-fit items-center gap-2 rounded-lg bg-[#087c83] px-5 py-4 text-xs font-bold uppercase tracking-wider text-white" data-id="element-257">
                {request}
                <ArrowRightIcon size={16} data-id="element-258" />
              </Link>
            </div>
            <img src={data.image} alt="" className="h-full min-h-[340px] w-full object-cover" data-id="element-259" />
          </div>
        </section>
        <section className="mx-auto max-w-[1100px] px-6 py-20" data-id="element-260">
          <div className="grid gap-8 md:grid-cols-2" data-id="element-261">
            {data.sections.map(section => <article key={section.title.fr} className="border-t-4 border-[#087c83] bg-white p-7 shadow-sm" data-id="element-262">
                <CheckCircle2Icon className="text-[#087c83]" data-id="element-263" />
                <h2 className="mt-5 text-2xl font-extrabold text-[#102b43]" data-id="element-264">
                  {section.title[locale]}
                </h2>
                <p className="mt-3 leading-7 text-slate-600" data-id="element-265">
                  {section.body[locale]}
                </p>
              </article>)}
          </div>
        </section>
      </main>
    </SiteLayout>;
}